import { Check, X } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { PASSWORD_REQUIREMENTS, getPasswordStrength } from "@/lib/passwordPolicy";
import { cn } from "@/lib/utils";

interface PasswordRequirementsProps {
  password: string;
  /** Hide the strength bar and only show the checklist. */
  showStrength?: boolean;
  className?: string;
}

export const PasswordRequirements = ({
  password,
  showStrength = true,
  className,
}: PasswordRequirementsProps) => {
  const strength = getPasswordStrength(password);

  return (
    <div className={cn("space-y-2", className)} aria-live="polite">
      {showStrength && password.length > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Password strength</span>
            <span
              className={
                !strength.meetsPolicy
                  ? "text-destructive"
                  : strength.label === "Strong"
                  ? "text-accent"
                  : "text-yellow-600"
              }
            >
              {strength.label}
            </span>
          </div>
          <Progress value={strength.score} className="h-2" />
        </div>
      )}
      <ul className="space-y-1">
        {PASSWORD_REQUIREMENTS.map((req) => {
          const met = req.test(password ?? "");
          return (
            <li
              key={req.id}
              className={cn(
                "flex items-center gap-2 text-xs",
                met ? "text-accent" : "text-muted-foreground"
              )}
            >
              {met ? (
                <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              ) : (
                <X className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
              )}
              <span>{req.label}</span>
              <span className="sr-only">{met ? " (met)" : " (not met)"}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PasswordRequirements;
